import React, { useRef } from 'react';
import { Button } from '@fiverr-private/button';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Box } from '@fiverr-private/theme';
import { ElementType } from '@fiverr-private/bianka_js';
import { useEnteredView } from '@fiverr-private/hooks';
import DragScrollCarousel, { ExtendedResponsiveSpacing } from '../../DragScrollCarousel';
import { sendBiankaImpression, sendBiankaClick } from '../../../shared/biEvents';
import { EVENT_NAMES } from '../../../shared/biEvents/eventNames';
import { sendMixpanelEvent } from '../utils/mixpanel';
import { MIXPANEL_EVENTS } from '../utils/eventNames';
import { enrichUrlWithParams } from '../../../shared/urlEnrichment';
import { CATEGORY_LINKS } from './constants';
import { buttonClass, badgeClass } from './styles.ve.css';

const SearchCategoriesLinks: React.FC = () => {
  const observableRef = useRef<HTMLDivElement>(null);

  useEnteredView(observableRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.POPULAR_SERVICES_TAGS_ARE_SHOWN,
        componentName: 'popular_services_tags',
        componentText: 'Popular Services Tags',
        isV16: true,
      });
    },
  });

  const handleCategoryClick = (category: (typeof CATEGORY_LINKS)[0], index: number) => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_POPULAR_SERVICES_TAG,
      elementName: 'popular_services_tag',
      elementType: ElementType.BUTTON,
      elementText: category.title,
      position: index + 1,
      isV16: true,
    });

    sendMixpanelEvent({
      eventName: MIXPANEL_EVENTS.CLICKED_POPULAR_SERVICES_TAGS,
      clickedElement: category.title,
      position: index + 1,
    });
  };

  const renderCategoryButton = (category: (typeof CATEGORY_LINKS)[0], index: number) => (
    <Button
      key={category.href}
      href={enrichUrlWithParams(category.href, 'header_lohp')}
      target="_blank"
      variant="outline"
      className={buttonClass}
      onDragStart={(e) => e.preventDefault()}
      onClick={() => handleCategoryClick(category, index)}
    >
      {category.title}
      {category.badge && <span className={badgeClass}>{category.badge}</span>}
    </Button>
  );

  return (
    <div ref={observableRef}>
      <Box display={{ default: 'block', md: 'none' }}>
        <DragScrollCarousel
          gap={{ default: '3', sm: '4' } as unknown as ExtendedResponsiveSpacing}
          itemWidth="auto"
          showFadeOverlay={false}
        >
          {CATEGORY_LINKS.map((category, index) => (
            <Container padding="0" key={category.href}>
              {renderCategoryButton(category, index)}
            </Container>
          ))}
        </DragScrollCarousel>
      </Box>

      <Box display={{ default: 'none', md: 'block' }}>
        <Stack direction="column" gap="4" alignItems="center" wrap="nowrap">
          <Stack direction="row" gap="4" justifyContent="center" wrap="nowrap">
            {CATEGORY_LINKS.slice(0, 4).map((category, index) => renderCategoryButton(category, index))}
          </Stack>
          <Stack direction="row" gap="4" justifyContent="center" wrap="nowrap">
            {CATEGORY_LINKS.slice(4).map((category, index) => renderCategoryButton(category, index + 4))}
          </Stack>
        </Stack>
      </Box>
    </div>
  );
};

export default SearchCategoriesLinks;
